import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import * as PhotoSwipe from 'photoswipe';
import { concatFilePath, getPhotoDisplayUrl } from 'src/app/shared/utils/util-function';
import * as PhotoSwipeUI_Default from 'photoswipe/dist/photoswipe-ui-default';
import { GlobalStateService } from 'src/app/services/global-state.service';


export class PhotoSwipeImage {
  src: string;
  w: number;
  h: number;
  currentImage: any;
  /** Placeholder PhotoSwipe shows while `src` loads. */
  msrc?: string;
  /** Capped -512 rendition browsed by default; `src` starts here. */
  thumbSrc?: string;
  /** Full-resolution original, lazily swapped in for the slide being viewed. */
  originalSrc?: string;
  /** Guard so the full-res original is only preloaded once at a time. */
  fullResLoading?: boolean;

  constructor(src: string, w: number, h: number, currentImage: any) {
    this.src = src;
    this.w = w;
    this.h = h;
    this.currentImage = currentImage;
  }
}

@Component({
  selector: 'projul-lightbox',
  templateUrl: './lightbox.component.html',
  styleUrls: ['./lightbox.component.scss']
})
export class LightboxComponent implements OnInit {
  @Input() galleryKey: string;
  isBrowser: boolean;
  key: any;
  currentFileRef: any;
  pswpInstance: any;
  lightBoxSubscription: Subscription = null as any;
  hasPermission: boolean = false;
  @ViewChild('editMarkerBtn') editMarkerBtn:ElementRef;
  isCustomer:boolean = false;
  currentFileRefBackup: any;
  lightBoxVisible = true;
  getImagesListToLighbox :any
  private static readonly FULL_RES_UPGRADE_DELAY_MS = 300;
  private fullResUpgradeTimer: any = null;

  constructor(private globalStateService : GlobalStateService) { }

  ngOnInit(): void {
  }


  openImage(index: number, img: any,isGallery?:boolean,isNoteList:boolean = false) {
    if(isGallery) this.galleryKey = 'galleryKey';
    else if(isNoteList) this.galleryKey = 'notelistKey';
    else this.galleryKey = 'singleImage';
    this.lightBoxVisible = true;
    this.getImagesListToLighbox = this.globalStateService.openLightBoxForNote$?.value
    setTimeout(()=> {
      if(this.lightBoxVisible !== true){
        return;
      }
      try {
        this.openPhotoSwipe(index, img, document.getElementsByClassName('ngx_photoswipe')[0]);
        this.registerListeners();
      } catch (error) {
        this.abortLightbox(error);
        alert('This photo could not be opened. Please try again.');
      }
    },20)
  }

  /** A viewer that failed part-way is torn down, so its open root can never sit over the page. */
  private abortLightbox(error: unknown): void {
    const pswp = this.pswpInstance;
    this.pswpInstance = null;
    clearTimeout(this.fullResUpgradeTimer);
    this.fullResUpgradeTimer = null;
    this.lightBoxVisible = false;
    console.error('Lightbox: the photo viewer failed to open', error);
    try {
      pswp?.close();
    } catch (closeError) {
      console.error('Lightbox: a failed photo viewer could not be closed', closeError);
      pswp?.destroy();
    }
  }
  registerListeners() {
    let currentRef = this;
    
    this.pswpInstance.listen('afterChange', function () {
      currentRef.currentFileRef = currentRef.pswpInstance.currItem.currentImage;

    });
  }

  private openPhotoSwipe(index: number, img: any, galleryDOM: any): boolean {

    const options: PhotoSwipe.Options = {};
    options.galleryUID = galleryDOM.getAttribute('data-pswp-uid');
    options.index = index;
    // With no thumbnail to zoom from, an animated open binds its document/window events on a timer a fast close cannot cancel.
    options.showAnimationDuration = 0;
    this.currentFileRef = img;
    const PSWP: HTMLElement = document.querySelectorAll('.pswp')[0] as HTMLElement;
    if (this.galleryKey === 'galleryKey' || this.galleryKey === 'notelistKey') {
      let swipes = this.getImagesAsPhotoSwipe();
      this.intializeLightBox(PSWP, swipes, options);
    }
    else {
      const items: Array<PhotoSwipeImage> = [];
      items.push(this.toPhotoSwipeItem(img));
      this.intializeLightBox(PSWP, items, options);
    }
    return false;
  }

  private listenForClose(pswp: any): void {
    pswp.listen('close', () => {
      clearTimeout(this.fullResUpgradeTimer);
      this.fullResUpgradeTimer = null;
      setTimeout(() => {
        if (this.lightBoxVisible) {
          pswp.cleanSlide(this.currentFileRef);
          this.lightBoxVisible = false;
        }
      }, 10);
    });
  }

  private intializeLightBox(PSWP: HTMLElement, swipes: PhotoSwipeImage[], options: PhotoSwipe.Options) {
    this.pswpInstance = new PhotoSwipe(PSWP, PhotoSwipeUI_Default, swipes, options);
    this.listenForClose(this.pswpInstance);
    this.pswpInstance.listen('imageLoadComplete', (index:any, item:any) => {
      if (!item.w || !item.h) {
        let img = new Image();
        img.onload = () => {
          item.w = img.width;
          item.h = img.height;
          this.pswpInstance.updateSize(true);
          swipes[index] = {
            ...swipes[index],
            w: img.width,
            h: img.height
          };
        };
        img.src = item.src;
      }
    });
    this.pswpInstance.listen('afterChange', () => this.scheduleFullResUpgrade());
    this.pswpInstance.init();
    this.scheduleFullResUpgrade();
  }

  private getImagesAsPhotoSwipe(): Array<PhotoSwipeImage> {
    const items: Array<PhotoSwipeImage> = [];
    this.getImagesListToLighbox?.forEach((img: any) => {
      items.push(this.toPhotoSwipeItem(img));
    });

    return items;
  }

  /**
   * Starts on the capped -512 rendition: registering originals for a whole report decodes hundreds
   * of MB of bitmaps and OOM-kills mobile browsers. Only the settled slide is upgraded.
   */
  private toPhotoSwipeItem(img: any): PhotoSwipeImage {
    const originalSrc = concatFilePath(img.UriPath, img.FileName);
    const thumbSrc = getPhotoDisplayUrl(img) || originalSrc;
    const item = new PhotoSwipeImage(thumbSrc, (img.Width ?? img.ImageWidth) || 0, (img.Height ?? img.ImageHeight) || 0, img);
    item.msrc = thumbSrc;
    item.thumbSrc = thumbSrc;
    item.originalSrc = originalSrc;
    return item;
  }

  private scheduleFullResUpgrade(): void {
    clearTimeout(this.fullResUpgradeTimer);
    const pswp = this.pswpInstance;
    if (!pswp) {
      return;
    }
    // Delay so photos flicked past never download/decode their original, and so
    // PhotoSwipe's deferred image-append pool has flushed before slides are released.
    this.fullResUpgradeTimer = setTimeout(() => {
      this.releaseFarSlides(pswp);
      this.upgradeCurrentSlideToFullRes(pswp);
    }, LightboxComponent.FULL_RES_UPGRADE_DELAY_MS);
  }

  /**
   * Preloads off-DOM so a healthy slide only sharpens, never blanks. Also the only workable
   * recovery point for a 404'd thumbnail: PhotoSwipe never fires imageLoadComplete for one.
   */
  private upgradeCurrentSlideToFullRes(pswp: any): void {
    if (this.pswpInstance !== pswp) {
      return;
    }
    const item = pswp.currItem;
    if (!item || !item.originalSrc || item.fullResLoading) {
      return;
    }
    // Already showing the original without error — nothing to do.
    if (item.src === item.originalSrc && !item.loadError) {
      return;
    }
    item.fullResLoading = true;
    const loader = new Image();
    loader.onload = () => {
      item.fullResLoading = false;
      if (this.pswpInstance !== pswp || pswp.currItem !== item) {
        return;
      }
      const dimsWereUndersized = loader.naturalWidth > (item.w || 0) + 1;
      item.src = item.originalSrc;

      if (item.loadError) {
        // The only path that may re-append, and it must, to replace PhotoSwipe's error text.
        // The slide was already blank, so nothing flashes.
        item.loadError = false;
        item.w = loader.naturalWidth;
        item.h = loader.naturalHeight;
        item.loaded = false;
        pswp.invalidateCurrItems();
        pswp.updateSize(true);
        return;
      }

      // Healthy slide: sharpen the visible <img> in place so an active zoom is never
      // reset and the slide never blanks.
      const el = item.container && item.container.querySelector('img.pswp__img:not(.pswp__img--placeholder)') as HTMLImageElement;
      if (!el) {
        // Slide isn't laid out yet — let PhotoSwipe render the original itself.
        item.w = loader.naturalWidth;
        item.h = loader.naturalHeight;
        item.loaded = false;
        pswp.invalidateCurrItems();
        pswp.updateSize(true);
        return;
      }
      el.src = item.originalSrc;
      if (dimsWereUndersized) {
        // Declared size came from the thumbnail, so correct it and let updateSize() reflow the
        // existing <img> in place rather than re-appending, which would black out the slide.
        item.w = loader.naturalWidth;
        item.h = loader.naturalHeight;
        pswp.updateSize(true);
      }
    };
    loader.onerror = () => {
      item.fullResLoading = false;
    };
    loader.src = item.originalSrc;
  }

  /**
   * Rewrites item.src only, for slides PhotoSwipe has already released (!item.container). Touching
   * containers, imgs or loaded flags races its own DOM pool and blanks freshly-shown slides.
   */
  private releaseFarSlides(pswp: any): void {
    if (this.pswpInstance !== pswp || !Array.isArray(pswp.items)) {
      return;
    }
    const count = pswp.items.length;
    if (count <= 3) {
      return;
    }
    const curr = pswp.getCurrentIndex();
    const kept = new Set([(curr + count - 1) % count, curr, (curr + 1) % count]);
    pswp.items.forEach((item: any, index: number) => {
      if (!item || kept.has(index) || item.container || item.fullResLoading) {
        return;
      }
      if (item.thumbSrc && item.src !== item.thumbSrc) {
        item.src = item.thumbSrc;
        item.loaded = false;
      }
    });
  }

  downloadFile() {

    if (this.currentFileRef) {
      this.downloadFileFromUrl(
        concatFilePath(this.currentFileRef.UriPath , this.currentFileRef.FileName),
        this.currentFileRef.FileName
      );
    }

  }

  downloadFileFromUrl(fileUrl: string, fileName: string) {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', fileUrl, true);
    xhr.responseType = 'blob';
    xhr.onload = function () {
      let urlCreator = window.URL || window.webkitURL;
      let imageUrl = urlCreator.createObjectURL(this.response);
      let tag = document.createElement('a');
      tag.href = imageUrl;
      tag.download = fileName;
      document.body.appendChild(tag);
      tag.click();
      document.body.removeChild(tag);
    };
    xhr.send();
  }
}
